import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { lazy, Suspense } from 'react';
import PageTransition from './PageTransition';
import ProtectedRoute from './ProtectedRoute';
import AdminProtectedRoute from '@/components/admin/AdminProtectedRoute';
import { useShopEnabled, useBibResaleEnabled } from '@/hooks/usePlatformSettings';

// Eagerly loaded: homepage (critical path)
import Index from '@/pages/Index';

// Lazy-loaded pages
const NotFound = lazy(() => import('@/pages/NotFound'));
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const EventDetailPage = lazy(() => import('@/pages/EventDetailPage'));
const EventResultsPage = lazy(() => import('@/pages/EventResultsPage'));
const OrganizationPage = lazy(() => import('@/pages/OrganizationPage'));
const OrganizationsPage = lazy(() => import('@/pages/OrganizationsPage'));
const OAuthConsent = lazy(() => import('@/pages/OAuthConsent'));
const OrganizerSignup = lazy(() => import('@/pages/organizer/OrganizerSignup'));
const OrganizerDashboard = lazy(() => import('@/pages/organizer/OrganizerDashboard'));
const OrganizerEvents = lazy(() => import('@/pages/organizer/OrganizerEvents'));
const OrganizerParticipants = lazy(() => import('@/pages/organizer/OrganizerParticipants'));
const OrganizerBibs = lazy(() => import('@/pages/organizer/OrganizerBibs'));
const OrganizerStats = lazy(() => import('@/pages/organizer/OrganizerStats'));
const CreateEventWizard = lazy(() => import('@/pages/organizer/CreateEventWizard'));
const CreateEventChoice = lazy(() => import('@/pages/organizer/CreateEventChoice'));
const VerificationStatus = lazy(() => import('@/pages/organizer/VerificationStatus'));
const DossardHub = lazy(() => import('@/pages/organizer/DossardHub'));
const DossardHubOrder = lazy(() => import('@/pages/organizer/DossardHubOrder'));
const DossardHubOrderDetail = lazy(() => import('@/pages/organizer/DossardHubOrderDetail'));
const MerchHub = lazy(() => import('@/pages/organizer/MerchHub'));
const MerchHubRequest = lazy(() => import('@/pages/organizer/MerchHubRequest'));
const MerchHubRequestDetail = lazy(() => import('@/pages/organizer/MerchHubRequestDetail'));
const EventPreview = lazy(() => import('@/pages/organizer/EventPreview'));
const OrganizerCommunication = lazy(() => import('@/pages/organizer/OrganizerCommunication'));
const OrganizerVolunteers = lazy(() => import('@/pages/organizer/OrganizerVolunteers'));
const EventBudget = lazy(() => import('@/pages/organizer/EventBudget'));
const EventPlanning = lazy(() => import('@/pages/organizer/EventPlanning'));
const EventResults = lazy(() => import('@/pages/organizer/EventResults'));
const OrganizerProfile = lazy(() => import('@/pages/organizer/OrganizerProfile'));
const OrganizerHelp = lazy(() => import('@/pages/organizer/OrganizerHelp'));
const OrganizerPromoCodes = lazy(() => import('@/pages/organizer/OrganizerPromoCodes'));
const OrganizerFinances = lazy(() => import('@/pages/organizer/OrganizerFinances'));
const AuthChoicePage = lazy(() => import('@/pages/auth/AuthChoicePage'));
const RunnerOnboarding = lazy(() => import('@/pages/auth/RunnerOnboarding'));
const OrganizerOnboarding = lazy(() => import('@/pages/auth/OrganizerOnboarding'));
const VolunteerOnboarding = lazy(() => import('@/pages/auth/VolunteerOnboarding'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const RunnerDashboard = lazy(() => import('@/pages/runner/RunnerDashboard'));
const RunnerRegistrations = lazy(() => import('@/pages/runner/RunnerRegistrations'));
const RunnerProfile = lazy(() => import('@/pages/runner/RunnerProfile'));
const RunnerRecommendations = lazy(() => import('@/pages/runner/RunnerRecommendations'));
const RunnerSearch = lazy(() => import('@/pages/runner/RunnerSearch'));
const RunnerProofs = lazy(() => import('@/pages/runner/RunnerProofs'));
const VolunteerDashboard = lazy(() => import('@/pages/volunteer/VolunteerDashboard'));
const VolunteerSearch = lazy(() => import('@/pages/volunteer/VolunteerSearch'));
const VolunteerMissions = lazy(() => import('@/pages/volunteer/VolunteerMissions'));
const VolunteerApplications = lazy(() => import('@/pages/volunteer/VolunteerApplications'));
const VolunteerProfile = lazy(() => import('@/pages/volunteer/VolunteerProfile'));
const VolunteerInvitationAccept = lazy(() => import('@/pages/volunteer/VolunteerInvitationAccept'));
const ExplorerPage = lazy(() => import('@/pages/ExplorerPage'));
const PastEventsPage = lazy(() => import('@/pages/PastEventsPage'));
const CommunityExplorerPage = lazy(() => import('@/pages/CommunityExplorerPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const FAQPage = lazy(() => import('@/pages/FAQPage'));
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const CookiesPage = lazy(() => import('@/pages/CookiesPage'));
const PaymentCallback = lazy(() => import('@/pages/payment/PaymentCallback'));
const MapTestPage = lazy(() => import('@/pages/MapTestPage'));
const VerificationPage = lazy(() => import('@/pages/VerificationPage'));
const TeamJoinPage = lazy(() => import('@/pages/TeamJoinPage'));

// Admin pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminOrganizations = lazy(() => import('@/pages/admin/AdminOrganizations'));
const AdminEvents = lazy(() => import('@/pages/admin/AdminEvents'));
const AdminEventEdit = lazy(() => import('@/pages/admin/AdminEventEdit'));
const AdminEventHistory = lazy(() => import('@/pages/admin/AdminEventHistory'));
const AdminVolunteers = lazy(() => import('@/pages/admin/AdminVolunteers'));
const AdminRegistrations = lazy(() => import('@/pages/admin/AdminRegistrations'));
const AdminPayments = lazy(() => import('@/pages/admin/AdminPayments'));
const AdminLogs = lazy(() => import('@/pages/admin/AdminLogs'));
const AdminActivityLog = lazy(() => import('@/pages/admin/AdminActivityLog'));
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'));
const EmailTemplatesPage = lazy(() => import('@/pages/admin/EmailTemplatesPage'));
const AdminMerchCatalog = lazy(() => import('@/pages/admin/AdminMerchCatalog'));
const AdminPayouts = lazy(() => import('@/pages/admin/AdminPayouts'));
const AdminPaymentsAudit = lazy(() => import('@/pages/admin/AdminPaymentsAudit'));
const AdminCRM = lazy(() => import('@/pages/admin/AdminCRM'));
const AdminCreditWithdrawals = lazy(() => import('@/pages/admin/AdminCreditWithdrawals'));
const AdminEmailLogs = lazy(() => import('@/pages/admin/AdminEmailLogs'));
const AdminShopPage = lazy(() => import('@/pages/admin/AdminShopPage'));
const AdminBibResalePage = lazy(() => import('@/pages/admin/AdminBibResalePage'));
const AdminLoadTest = lazy(() => import('@/pages/admin/AdminLoadTest'));
const AdminBroadcast = lazy(() => import('@/pages/admin/AdminBroadcast'));
const AdminExternalEvents = lazy(() => import('@/pages/admin/AdminExternalEvents'));
const CalendrierPage = lazy(() => import('@/pages/CalendrierPage'));

// Bib resale
const BibListingsPage = lazy(() => import('@/pages/bib-resale/BibListingsPage'));
const BibListingDetailPage = lazy(() => import('@/pages/bib-resale/BibListingDetailPage'));
const MySalesPage = lazy(() => import('@/pages/runner/MySalesPage'));
const CreditsPage = lazy(() => import('@/pages/runner/CreditsPage'));
const CompletePendingTransferPage = lazy(() => import('@/pages/runner/CompletePendingTransferPage'));
const RunnerBibAlerts = lazy(() => import('@/pages/runner/RunnerBibAlerts'));
const EventBibResalePage = lazy(() => import('@/pages/organizer/EventBibResalePage'));
const BoutiquePage = lazy(() => import('@/pages/BoutiquePage'));
const ShopProductPage = lazy(() => import('@/pages/ShopProductPage'));
const ShopCartPage = lazy(() => import('@/pages/ShopCartPage'));
const ShopOrderConfirmation = lazy(() => import('@/pages/ShopOrderConfirmation'));

const AnimatedRoutes = () => {
  const location = useLocation();
  const { shopEnabled } = useShopEnabled();
  const { bibResaleEnabled } = useBibResaleEnabled();

  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/explorer" element={<ExplorerPage />} />
          <Route path="/evenements-passes" element={<PageTransition><PastEventsPage /></PageTransition>} />
          <Route path="/calendrier" element={<PageTransition><CalendrierPage /></PageTransition>} />
          <Route path="/communaute" element={<CommunityExplorerPage />} />
          <Route path="/a-propos" element={<PageTransition><AboutPage /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
          <Route path="/faq" element={<PageTransition><FAQPage /></PageTransition>} />
          <Route path="/mentions-legales" element={<PageTransition><LegalPage /></PageTransition>} />
          <Route path="/cookies" element={<PageTransition><CookiesPage /></PageTransition>} />
          <Route path="/auth" element={<PageTransition><AuthChoicePage /></PageTransition>} />
          <Route path="/auth/coureur" element={<PageTransition><RunnerOnboarding /></PageTransition>} />
          <Route path="/auth/organisateur" element={<PageTransition><OrganizerOnboarding /></PageTransition>} />
          <Route path="/auth/benevole" element={<PageTransition><VolunteerOnboarding /></PageTransition>} />
          <Route path="/auth/connexion" element={<PageTransition><LoginPage /></PageTransition>} />
          <Route path="/auth/mot-de-passe-oublie" element={<PageTransition><ForgotPasswordPage /></PageTransition>} />
          <Route path="/auth/nouveau-mot-de-passe" element={<PageTransition><ResetPasswordPage /></PageTransition>} />
          <Route path="/auth/legacy" element={<PageTransition><AuthPage /></PageTransition>} />
          
          {/* Detail pages */}
          <Route path="/evenement/:id" element={<PageTransition variant="fade"><EventDetailPage /></PageTransition>} />
          <Route path="/evenement/:id/resultats" element={<PageTransition variant="fade"><EventResultsPage /></PageTransition>} />
          <Route path="/org/:slug" element={<PageTransition variant="scale"><OrganizationPage /></PageTransition>} />
          <Route path="/organizations" element={<PageTransition><OrganizationsPage /></PageTransition>} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          <Route path="/rejoindre-equipe" element={<PageTransition><TeamJoinPage /></PageTransition>} />
          <Route path="/boutique" element={shopEnabled ? <PageTransition><BoutiquePage /></PageTransition> : <Navigate to="/" replace />} />
          <Route path="/boutique/:slug" element={shopEnabled ? <PageTransition><ShopProductPage /></PageTransition> : <Navigate to="/" replace />} />
          <Route path="/boutique/panier" element={shopEnabled ? <ProtectedRoute><ShopCartPage /></ProtectedRoute> : <Navigate to="/" replace />} />
          <Route path="/boutique/commande/:id" element={shopEnabled ? <ProtectedRoute><PageTransition><ShopOrderConfirmation /></PageTransition></ProtectedRoute> : <Navigate to="/" replace />} />

          {/* Bib resale - public */}
          <Route path="/bourse-dossards" element={bibResaleEnabled ? <PageTransition><BibListingsPage /></PageTransition> : <Navigate to="/" replace />} />
          <Route path="/bourse-dossards/:listingId" element={bibResaleEnabled ? <PageTransition><BibListingDetailPage /></PageTransition> : <Navigate to="/" replace />} />
          
          {/* Organizer routes */}
          <Route path="/organisateur/inscription" element={<OrganizerSignup />} />
          <Route path="/organisateur/tableau-de-bord" element={<OrganizerDashboard />} />
          <Route path="/organisateur/evenements" element={<OrganizerEvents />} />
          <Route path="/organisateur/participants" element={<OrganizerParticipants />} />
          <Route path="/organisateur/dossards" element={<OrganizerBibs />} />
          <Route path="/organisateur/statistiques" element={<OrganizerStats />} />
          <Route path="/organisateur/evenements/creer" element={<CreateEventChoice />} />
          <Route path="/organisateur/evenements/nouveau" element={<CreateEventWizard />} />
          <Route path="/organisateur/evenements/:id" element={<CreateEventWizard />} />
          <Route path="/organisateur/evenements/:id/apercu" element={<EventPreview />} />
          <Route path="/organisateur/evenements/:id/budget" element={<EventBudget />} />
          <Route path="/organisateur/evenements/:id/planning" element={<EventPlanning />} />
          <Route path="/organisateur/evenements/:id/resultats" element={<EventResults />} />
          <Route path="/organisateur/verification" element={<VerificationStatus />} />
          <Route path="/organisateur/communication" element={<OrganizerCommunication />} />
          <Route path="/organisateur/benevoles" element={<OrganizerVolunteers />} />
          <Route path="/organisateur/dossardhub" element={<DossardHub />} />
          <Route path="/organisateur/dossardhub/commander" element={<DossardHubOrder />} />
          <Route path="/organisateur/dossardhub/commande/:id" element={<DossardHubOrderDetail />} />
          <Route path="/organisateur/merchhub" element={<MerchHub />} />
          <Route path="/organisateur/merchhub/demande" element={<MerchHubRequest />} />
          <Route path="/organisateur/merchhub/demande/:id" element={<MerchHubRequestDetail />} />
          <Route path="/organisateur/profil" element={<OrganizerProfile />} />
          <Route path="/organisateur/aide" element={<OrganizerHelp />} />
          <Route path="/organisateur/codes-promo" element={<OrganizerPromoCodes />} />
          <Route path="/organisateur/finances" element={<OrganizerFinances />} />
          
          {/* Runner routes */}
          <Route path="/coureur/tableau-de-bord" element={<ProtectedRoute><RunnerDashboard /></ProtectedRoute>} />
          <Route path="/coureur/trouver" element={<ProtectedRoute><RunnerSearch /></ProtectedRoute>} />
          <Route path="/coureur/inscriptions" element={<ProtectedRoute><RunnerRegistrations /></ProtectedRoute>} />
          <Route path="/coureur/profil" element={<ProtectedRoute><RunnerProfile /></ProtectedRoute>} />
          <Route path="/coureur/recommandations" element={<ProtectedRoute><RunnerRecommendations /></ProtectedRoute>} />
          <Route path="/coureur/justificatifs" element={<ProtectedRoute><RunnerProofs /></ProtectedRoute>} />
          <Route path="/coureur/mes-ventes" element={<ProtectedRoute><MySalesPage /></ProtectedRoute>} />
          <Route path="/coureur/mes-alertes" element={<ProtectedRoute><RunnerBibAlerts /></ProtectedRoute>} />
          <Route path="/coureur/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
          <Route path="/coureur/finaliser-transfert/:listingId" element={<ProtectedRoute><CompletePendingTransferPage /></ProtectedRoute>} />

          {/* Organizer bib resale */}
          <Route path="/organisateur/evenements/:id/bourse" element={<EventBibResalePage />} />
          
          {/* Public verification page */}
          <Route path="/v/:shortId" element={<PageTransition><VerificationPage /></PageTransition>} />
          
          {/* Volunteer routes */}
          <Route path="/benevole/tableau-de-bord" element={<ProtectedRoute><VolunteerDashboard /></ProtectedRoute>} />
          <Route path="/benevole/trouver" element={<ProtectedRoute><VolunteerSearch /></ProtectedRoute>} />
          <Route path="/benevole/missions" element={<ProtectedRoute><VolunteerMissions /></ProtectedRoute>} />
          <Route path="/benevole/candidatures" element={<ProtectedRoute><VolunteerApplications /></ProtectedRoute>} />
          <Route path="/benevole/profil" element={<ProtectedRoute><VolunteerProfile /></ProtectedRoute>} />
          <Route path="/benevole/invitations/:token" element={<PageTransition><VolunteerInvitationAccept /></PageTransition>} />
          
          {/* Payment routes */}
          <Route path="/paiement/callback" element={<PageTransition><PaymentCallback /></PageTransition>} />
          
          {/* Admin routes */}
          <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
          <Route path="/admin/users" element={<AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>} />
          <Route path="/admin/organizations" element={<AdminProtectedRoute><AdminOrganizations /></AdminProtectedRoute>} />
          <Route path="/admin/events" element={<AdminProtectedRoute><AdminEvents /></AdminProtectedRoute>} />
          <Route path="/admin/events/:id" element={<AdminProtectedRoute><AdminEventEdit /></AdminProtectedRoute>} />
          <Route path="/admin/events/:id/historique" element={<AdminProtectedRoute><AdminEventHistory /></AdminProtectedRoute>} />
          <Route path="/admin/volunteers" element={<AdminProtectedRoute><AdminVolunteers /></AdminProtectedRoute>} />
          <Route path="/admin/registrations" element={<AdminProtectedRoute><AdminRegistrations /></AdminProtectedRoute>} />
          <Route path="/admin/payments" element={<AdminProtectedRoute><AdminPayments /></AdminProtectedRoute>} />
          <Route path="/admin/logs" element={<AdminProtectedRoute><AdminLogs /></AdminProtectedRoute>} />
          <Route path="/admin/activity-log" element={<AdminProtectedRoute><AdminActivityLog /></AdminProtectedRoute>} />
          <Route path="/admin/settings" element={<AdminProtectedRoute><AdminSettings /></AdminProtectedRoute>} />
          <Route path="/admin/emails" element={<AdminProtectedRoute><EmailTemplatesPage /></AdminProtectedRoute>} />
          <Route path="/admin/merchhub" element={<AdminProtectedRoute><AdminMerchCatalog /></AdminProtectedRoute>} />
          <Route path="/admin/boutique" element={<AdminProtectedRoute><AdminShopPage /></AdminProtectedRoute>} />
          <Route path="/admin/bourse-dossards" element={<AdminProtectedRoute><AdminBibResalePage /></AdminProtectedRoute>} />
          <Route path="/admin/payouts" element={<AdminProtectedRoute><AdminPayouts /></AdminProtectedRoute>} />
          <Route path="/admin/payments-audit" element={<AdminProtectedRoute><AdminPaymentsAudit /></AdminProtectedRoute>} />
          <Route path="/admin/crm" element={<AdminProtectedRoute><AdminCRM /></AdminProtectedRoute>} />
          <Route path="/admin/courses-externes" element={<Navigate to="/admin/crm?tab=externes" replace />} />
          <Route path="/admin/credits" element={<AdminProtectedRoute><AdminCreditWithdrawals /></AdminProtectedRoute>} />
          <Route path="/admin/email-logs" element={<AdminProtectedRoute><AdminEmailLogs /></AdminProtectedRoute>} />
          <Route path="/admin/load-test" element={<AdminProtectedRoute><AdminLoadTest /></AdminProtectedRoute>} />
          <Route path="/admin/broadcast" element={<AdminProtectedRoute><AdminBroadcast /></AdminProtectedRoute>} />
          {/* Test routes */}
          <Route path="/test-map" element={<MapTestPage />} />
          
          {/* Catch-all */}
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
};

export default AnimatedRoutes;